import { Icon } from "@platform/design-system";
import { Badge, Box, Divider, IconButton, Tooltip } from "@mui/material";

/** The rail's two listings. Compose is an action, not a destination. */
export type CommsView = "messages" | "drafts";

export interface CommsRailProps {
  view: CommsView;
  onSelectView: (view: CommsView) => void;
  onCompose: () => void;
  /**
   * Unread threads and unsent drafts, shown as badges.
   *
   * Optional because no endpoint totals either one yet: a listing answers with
   * threads, not counts. Omitted renders the icon with no badge, which is the
   * honest reading of "not known" — a zero would assert there are none.
   */
  messageCount?: number;
  draftCount?: number;
}

/**
 * The drawer's left rail: compose, then the listings the body can show.
 *
 * Vertical and icon-only at 60px. The labels live in tooltips and
 * `aria-label` rather than under the glyphs, because the drawer gives the
 * thread list every pixel it can and three stacked words would cost more
 * width than the rail is worth.
 */
export const CommsRail = ({
  view,
  onSelectView,
  onCompose,
  messageCount,
  draftCount,
}: CommsRailProps): React.JSX.Element => (
  <Box
    data-testid="comms-rail"
    sx={{
      width: 60,
      flexShrink: 0,
      borderRight: 1,
      borderColor: "divider",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 2,
    }}
  >
    <Tooltip title="Compose">
      <IconButton aria-label="Compose a message" onClick={onCompose}>
        <Icon name="edit" />
      </IconButton>
    </Tooltip>

    <Divider flexItem sx={{ mx: 2 }} />

    <RailButton
      label="Messages"
      icon="mail"
      count={messageCount}
      selected={view === "messages"}
      onClick={() => onSelectView("messages")}
    />
    <RailButton
      label="Drafts"
      icon="pending"
      count={draftCount}
      selected={view === "drafts"}
      onClick={() => onSelectView("drafts")}
    />
  </Box>
);

interface RailButtonProps {
  label: string;
  icon: "mail" | "pending";
  count?: number;
  selected: boolean;
  onClick: () => void;
}

const RailButton = ({
  label,
  icon,
  count,
  selected,
  onClick,
}: RailButtonProps): React.JSX.Element => (
  <Tooltip title={label}>
    {/*
      `showZero` would still leave the 0 in the DOM for a screen reader to
      read out, so an empty listing passes no badge content at all. An unknown
      count is the same case: a 0 would claim there is nothing waiting.
    */}
    <Badge
      badgeContent={count ? count : undefined}
      color="primary"
      overlap="circular"
    >
      <IconButton
        aria-label={`Show ${label.toLowerCase()}`}
        aria-current={selected ? "true" : undefined}
        onClick={onClick}
        sx={
          selected
            ? { backgroundColor: "action.selected", color: "brandPrimary.main" }
            : undefined
        }
      >
        <Icon name={icon} />
      </IconButton>
    </Badge>
  </Tooltip>
);
